<template>
  <div class="form-container">
    <div class="form-header">
      <h2>Edit Video</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <VideoForm :channels="sortedChannels" ref="videoForm" />
      <div class="form-actions">
        <button type="submit" class="submit-button big">
          Save Changes
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { useTemplateRef, ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import VideoForm from '@/components/VideoForm.vue';
import { isSuperuser } from '@/scripts/permissions';
import { handleApiError } from '@/scripts/shared-utils';

const route = useRoute();
const router = useRouter();
const channels = ref([]);
const videoId = ref(null);
const videoForm = useTemplateRef('videoForm');

const sortedChannels = computed(() => {
  return [...channels.value].sort((a, b) => {
    return a.name.localeCompare(b.name);
  });
});

const handleSubmit = async () => {
  try {
    const editableFields = [
      'title',
      'logline',
      'description',
      'channel_id',
      'languages',
      'subtitles',
      'awards',
      'release_date',
      'production_company',
      'directors',
      'writers',
      'starring',
      'category',
      'content_rating',
      'motion_amount',
      'video_file_id',
      'trailer_file_id',
      'banner_url',
      'poster_url',
      'thumbnail_url',
      'screenshot1_url',
      'screenshot2_url',
      'screenshot3_url',
      'visibility',
      'review',
      'avail_start_date',
      'avail_end_date'
    ];
    
    const videoData = {};
    const formData = videoForm.value.formData;
    
    editableFields.forEach(field => {
      if (field in formData) {
        videoData[field] = formData[field];
      }
    });
    
    await axios.put(`/api/videos/${videoId.value}`, videoData);
    closeForm();
  } catch (error) {
    handleApiError(error, 'saving video');
    alert('Error saving video. Please try again.');
  }
};

const handleCancel = () => {
  router.back();
};

const closeForm = () => {
  router.push(isSuperuser() ? '/admin/publishing' : `/videos/${videoId.value}`);
};

const formatDateForInput = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const fetchChannels = async () => {
  try {
    const response = await axios.get('/api/channels');
    channels.value = response.data;
  } catch (error) {
    handleApiError(error, 'fetching channels');
  }
};

const fetchVideoData = async () => {
  try {
    const response = await axios.get(`/api/videos/${videoId.value}`);
    const videoData = response.data;

    // Format dates for input fields
    if (videoData.release_date) {
      videoData.release_date = formatDateForInput(videoData.release_date);
    }
    if (videoData.avail_start_date) {
      videoData.avail_start_date = formatDateForInput(videoData.avail_start_date);
    }
    if (videoData.avail_end_date) {
      videoData.avail_end_date = formatDateForInput(videoData.avail_end_date);
    }

    // Update form data
    videoForm.value.formData = {
      title: videoData.title,
      channel_id: videoData.channel_id,
      review: videoData.review,
      visibility: videoData.visibility,
      logline: videoData.logline,
      series_id: videoData.series_id,
      season_id: videoData.season_id,
      episode_number: videoData.episode_number,
      description: videoData.description,
      duration: videoData.duration,
      format: videoData.format,
      category: videoData.category,
      motion_amount: videoData.motion_amount,
      languages: videoData.languages,
      subtitles: videoData.subtitles,
      awards: videoData.awards,
      content_rating: videoData.content_rating,
      video_file_id: videoData.video_file_id,
      trailer_file_id: videoData.trailer_file_id,
      banner_url: videoData.banner_url,
      poster_url: videoData.poster_url,
      thumbnail_url: videoData.thumbnail_url,
      screenshot1_url: videoData.screenshot1_url,
      screenshot2_url: videoData.screenshot2_url,
      screenshot3_url: videoData.screenshot3_url,
      release_date: videoData.release_date,
      avail_start_date: videoData.avail_start_date,
      avail_end_date: videoData.avail_end_date,
      production_company: videoData.production_company,
      directors: videoData.directors,
      writers: videoData.writers,
      starring: videoData.starring
    };
  } catch (error) {
    handleApiError(error, 'fetching video');
  }
};

onMounted(async () => {
  if (!route.params.video_id) {
    console.error('Missing video ID');
    router.push('/');
  }
  videoId.value = route.params.video_id;
  if (isSuperuser()) {
    await fetchChannels();
  }
  await fetchVideoData();
});
</script>

<style scoped>
@import '../styles/shared-form.css';
</style>
